import { NavLink, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  LayoutDashboard,
  MessageSquare,
  Shield,
  Workflow,
  FileText,
  FolderKanban,
  LogOut,
  User,
} from 'lucide-react';
import { listDashboardRuns } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProjectSwitcher } from './ProjectSwitcher';
import { PWAInstallButton } from './PWAInstallButton';

const baseTabs = [
  { to: '/chat', end: false, icon: MessageSquare, label: 'Chat' },
  { to: '/dashboard', end: true, icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/workflows', end: false, icon: Workflow, label: 'Workflows' },
  { to: '/artifacts', end: false, icon: FileText, label: 'Artifacts' },
  { to: '/work-areas', end: false, icon: FolderKanban, label: 'Work Areas' },
] as const;

export function TopNav(): React.ReactElement {
  const { isAdmin, user, logout, isAuthenticated } = useSession();
  const tabs = isAdmin
    ? [...baseTabs, { to: '/admin', end: false, icon: Shield, label: 'Admin' }]
    : baseTabs;

  // We only need `counts.running` — a server-side aggregate independent of
  // the `runs` array. `limit: 1` minimises the `runs` payload that the API
  // returns alongside the counts (we discard it).
  const { data: dashboardRuns } = useQuery({
    queryKey: ['dashboardRuns', { status: 'running', forCount: true }],
    queryFn: () => listDashboardRuns({ status: 'running', limit: 1 }),
    refetchInterval: 10_000,
  });
  const runningCount = dashboardRuns?.counts.running ?? 0;

  return (
    <nav className="relative flex items-center gap-1 border-b border-border bg-surface px-4">
      {/* Brand logo — compass/rotor glyph in Flight Deck palette */}
      <Link to="/chat" className="flex items-center gap-2 mr-4 hover:opacity-80 transition-opacity">
        <BrandMark size={22} />
        <span className="text-sm font-semibold text-text-primary tracking-tight">Metis</span>
      </Link>

      {tabs.map(({ to, end, icon: Icon, label }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }: { isActive: boolean }): string =>
            cn(
              'flex items-center gap-2 p-3 text-sm font-medium border-b-2 transition-colors',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            )
          }
        >
          <Icon className="size-4" />
          {label}
          {to === '/dashboard' && runningCount > 0 && (
            <span
              className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-accent-bright px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[oklch(0.14_0.02_245)]"
              aria-label={`${runningCount} workflows running`}
            >
              {runningCount}
            </span>
          )}
        </NavLink>
      ))}
      <div className="ml-auto flex items-center gap-3">
        <PWAInstallButton />
        <ProjectSwitcher />
        {isAuthenticated && user && (
          <div className="flex items-center gap-2 border-l border-border pl-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2 px-2 text-text-primary hover:bg-muted"
                  aria-label="Open account menu"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={`${user.githubLogin} avatar`}
                      className="size-6 rounded-full"
                    />
                  ) : (
                    <span
                      className="flex size-6 items-center justify-center rounded-full text-[10px] font-bold uppercase text-primary-foreground"
                      style={{
                        background: 'linear-gradient(135deg, var(--accent-bright), var(--flame))',
                      }}
                    >
                      {user.githubLogin.slice(0, 2)}
                    </span>
                  )}
                  <span
                    className="max-w-32 truncate text-sm"
                    title={user.email ?? user.githubLogin}
                  >
                    {user.githubLogin}
                  </span>
                  <ChevronDown className="size-3.5 text-text-tertiary" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-1">
                <div className="border-b border-border p-2">
                  <div className="truncate text-sm font-medium text-foreground">
                    {user.githubLogin}
                  </div>
                  {user.email && (
                    <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                  )}
                </div>
                <Link
                  to="/account"
                  className="mt-1 flex items-center gap-2 rounded-md p-2 text-sm text-text-secondary hover:bg-muted hover:text-text-primary"
                >
                  <User className="size-4" />
                  Account
                </Link>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm text-text-secondary hover:bg-muted hover:text-text-primary"
                >
                  <LogOut className="size-4" />
                  Sign out
                </button>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
      {/* Horizon hairline — sky→flame gradient tying the brand metaphor together */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
        style={{ background: 'var(--horizon)', opacity: 0.85 }}
      />
    </nav>
  );
}

function BrandMark({ size = 22 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: 'block' }}>
      <defs>
        <radialGradient id="metis-mark-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="var(--accent-bright)" stopOpacity="0.5" />
          <stop offset="1" stopColor="var(--accent-bright)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="11" fill="url(#metis-mark-glow)" />
      <circle cx="12" cy="12" r="2" fill="var(--flame)" />
      <path
        d="M12 3 L12 8 M12 16 L12 21 M3 12 L8 12 M16 12 L21 12"
        stroke="var(--accent-bright)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M5.5 5.5 L8.5 8.5 M15.5 15.5 L18.5 18.5 M5.5 18.5 L8.5 15.5 M15.5 8.5 L18.5 5.5"
        stroke="var(--accent-bright)"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
}
